import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { countryConfig } from '../../country';
import { LocaleLink as Link } from '../../i18n/locale-link';
import { Seo } from '../../components/seo';
import { Select } from '../../components/ui/select';
import { Pagination } from '../../components/pagination';
import { GoodStandingBadge } from '../../components/good-standing-badge';
import { useLocale, useT } from '../../i18n/locale-context';
import { usePageTitle } from '../../lib/page-title';
import { localePath } from '../../i18n/locale-path';
import { listingPageHref, resolveListingPage } from '../../lib/pagination-paths';
import { localizeRegion } from '../../lib/regions';
import { formatBudgetCompact, withCurrency } from '../../lib/format';
import {
  fetchCompanies,
  fetchCompanySectors,
  fetchCompanyRegions,
  type CompanyFilters,
  type CompanyListRow,
} from '../../lib/catalog-client';

const SITE_ORIGIN = countryConfig.brand.siteUrl;
const PAGE = 30;
// Radix Select reserves '' for the placeholder, so the "all" option needs a
// real sentinel value; it maps back to "no filter". The filters START on this
// value so each trigger shows a descriptive «Все …» default instead of a blank
// bar (an unmatched '' renders empty in the DS Select).
const ALL = '__all__';

/**
 * «Каталог участников госзакупок» — the browsable Good Standing directory
 * (cluster 2). Lists the ≥3-awards cohort with a trust badge; each row links to
 * the company's existing supplier/buyer profile (also de-orphaning those pages).
 *
 * PAGINATION. The unfiltered list is paginated by a CRAWLABLE path segment
 * (`/companies`, `/companies/page/2`, …) — the same shape category/region hubs
 * use and the prerender writes to disk — so every one of the ~13k companies
 * lands in a real HTML page a crawler can reach, not just the first 30. Filter
 * state lives in the query string (`?sector=…&page=…`); a filtered view is a
 * client interaction, canonicalised back to the bare `/companies` so filter
 * combinations never enter the index.
 */
export function Companies() {
  const t = useT();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const { page: pageParam } = useParams<{ page?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  usePageTitle(t('companies.title'));

  // Filters live in the URL so they survive pagination and are shareable; the
  // crawlable page number lives in the path segment (preferred over `?page`).
  const q = searchParams.get('q') ?? '';
  const sector = searchParams.get('sector') ?? '';
  const region = searchParams.get('region') ?? '';
  const role = searchParams.get('role') ?? '';
  const anyFilter = !!(q || sector || region || role);
  const page = resolveListingPage(pageParam, searchParams.get('page'));
  const offset = (page - 1) * PAGE;

  const filters: CompanyFilters = {
    q: q || undefined,
    sector: sector || undefined,
    region: region || undefined,
    role: (role || undefined) as CompanyFilters['role'],
    offset,
    limit: PAGE,
  };

  const { data, isLoading } = useQuery({
    queryKey: ['companies', filters],
    queryFn: () => fetchCompanies(filters),
    // A page change swaps `offset` (part of `filters`), changing the query key.
    // Without this the list collapses to empty for the fetch — and that height
    // collapse is what yanked the scroll to the top. Keeping the previous page
    // rendered while the next loads preserves scroll and prevents the flash.
    placeholderData: (prev) => prev,
  });
  const { data: sectors } = useQuery({
    queryKey: ['company-sectors'],
    queryFn: fetchCompanySectors,
  });
  const { data: regions } = useQuery({
    queryKey: ['company-regions'],
    queryFn: fetchCompanyRegions,
  });

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE));

  // Changing any facet resets to page 1 by navigating to the BARE /companies
  // path (dropping any `/page/N` route segment) with the updated query — a
  // `setSearchParams` alone would keep `/companies/page/3` and read `offset=60`
  // against the new filter, which shows an all-but-empty page.
  function setFilter(key: 'q' | 'sector' | 'region' | 'role', value: string | undefined) {
    const next = new URLSearchParams(searchParams);
    next.delete('page');
    if (value) next.set(key, value);
    else next.delete(key);
    const qs = next.toString();
    navigate(`${localePath('/companies', locale)}${qs ? `?${qs}` : ''}`);
  }

  // Debounce the search box into the URL so we don't push a history entry per
  // keystroke. `qInput` is the live field; it reflects external URL changes and
  // only writes back once typing settles.
  const [qInput, setQInput] = useState(q);
  useEffect(() => {
    setQInput(q);
  }, [q]);
  useEffect(() => {
    const id = setTimeout(() => {
      if (qInput !== q) setFilter('q', qInput.trim() || undefined);
    }, 400);
    return () => clearTimeout(id);
  }, [qInput]);

  // `/page/N` is crawlable; `?page=N` is the filtered-view fallback.
  function goToPage(nextPage: number) {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) next.delete('page');
    else next.set('page', String(nextPage));
    setSearchParams(next);
  }

  // Paged SEO ONLY for the unfiltered pager: filtered views canonicalise to the
  // bare directory so no filter combination competes for the index.
  const showPaged = !anyFilter && page > 1;

  // The capacity a row is shown in. Under «Заказчики» the API reads every row
  // as a buyer (its purchases, its buyer badge) and says so in `countsAs`; an
  // older API without the field gets the pre-2026-10-14 reading.
  const countsAsOf = (r: CompanyListRow): 'supplier' | 'buyer' =>
    r.countsAs ?? (r.role === 'buyer' ? 'buyer' : 'supplier');
  const profileHref = (r: CompanyListRow) =>
    countsAsOf(r) === 'buyer' ? `/buyer/${r.tin}` : `/supplier/${r.tin}`;
  // A «both» row also names what it did in the OTHER capacity, so a buyer
  // that also wins contracts says so instead of hiding it.
  const otherCapacity = (r: CompanyListRow): { as: 'supplier' | 'buyer'; count: number } | null => {
    if (r.role !== 'both' || r.countsAs == null) return null;
    const as = r.countsAs === 'buyer' ? 'supplier' : 'buyer';
    const count = as === 'supplier' ? r.supplierContracts : r.buyerContracts;
    return count != null && count > 0 ? { as, count } : null;
  };

  return (
    <>
      <Seo
        title={showPaged ? t('companies.seoTitlePaged', { page }) : t('companies.seoTitle')}
        description={
          showPaged
            ? t('companies.seoDescPaged', { page, totalPages, count: total })
            : t('companies.seoDesc')
        }
        canonical={anyFilter ? '/companies' : listingPageHref('/companies', page)}
        prev={!anyFilter && page > 1 ? listingPageHref('/companies', page - 1) : undefined}
        next={!anyFilter && page < totalPages ? listingPageHref('/companies', page + 1) : undefined}
        lang={locale}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: t('companies.seoTitle'),
          description: t('companies.seoDesc'),
          url: `${SITE_ORIGIN}${localePath(listingPageHref('/companies', page), locale)}`,
          inLanguage: locale,
        }}
      />
      <div className="mx-auto w-full max-w-6xl">
        <h1 className="text-[22px] font-bold leading-tight text-ink sm:text-[26px]">
          {t('companies.title')}
        </h1>
        <p className="mt-2 max-w-3xl text-[14px] leading-[1.6] text-ink-muted">
          {t('companies.intro')}
        </p>

        {/* Filter form — search + three facet selects in a 2×2 grid (single
            column on mobile). Each select starts on its «Все …» default so the
            trigger reads its purpose instead of sitting blank. */}
        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input
            type="text"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder={t('companies.search')}
            aria-label={t('companies.search')}
            className="w-full rounded-lg border border-edge bg-bone-surface px-3.5 py-2.5 text-[16px] text-ink placeholder:text-ink-muted"
          />
          <Select
            value={sector || ALL}
            onChange={(v) => setFilter('sector', v === ALL ? undefined : v)}
            aria-label={t('companies.filter.allSectors')}
            className="w-full py-2.5"
            options={[
              { value: ALL, label: t('companies.filter.allSectors') },
              ...(sectors ?? []).map((s) => ({ value: s.key, label: `${s.name} (${s.count})` })),
            ]}
          />
          <Select
            value={region || ALL}
            onChange={(v) => setFilter('region', v === ALL ? undefined : v)}
            aria-label={t('companies.filter.allRegions')}
            className="w-full py-2.5"
            options={[
              { value: ALL, label: t('companies.filter.allRegions') },
              ...(regions ?? []).map((r) => ({
                value: r.key,
                label: `${localizeRegion(r.name, locale) ?? r.name} (${r.count})`,
              })),
            ]}
          />
          <Select
            value={role || ALL}
            onChange={(v) => setFilter('role', v === ALL ? undefined : v)}
            aria-label={t('companies.filter.allRoles')}
            className="w-full py-2.5"
            options={[
              { value: ALL, label: t('companies.filter.allRoles') },
              { value: 'supplier', label: t('companies.role.supplier') },
              { value: 'buyer', label: t('companies.role.buyer') },
              { value: 'both', label: t('companies.role.both') },
            ]}
          />
        </div>

        <p className="mt-4 text-[13px] text-ink-muted">{t('companies.count', { count: total })}</p>

        {rows.length === 0 && !isLoading ? (
          <p className="mt-6 text-[14px] text-ink-muted">{t('companies.empty')}</p>
        ) : (
          <ul className="mt-2 divide-y divide-edge-light overflow-hidden rounded-xl border border-edge bg-bone-surface shadow-m-sm">
            {rows.map((r, i) => (
              <li key={r.tin}>
                <Link
                  to={profileHref(r)}
                  className="group flex flex-col gap-2 px-4 py-4 transition-colors hover:bg-bone-hover sm:flex-row sm:items-start sm:justify-between sm:gap-6 sm:px-5"
                >
                  {/* Identity: rank + name + registry/sector/region meta */}
                  <div className="flex min-w-0 gap-3 sm:gap-4">
                    <span className="w-6 shrink-0 pt-0.5 text-right text-[13px] tabular-nums text-ink-muted">
                      {offset + i + 1}
                    </span>
                    <div className="min-w-0">
                      {/* flex-WRAP so a long uppercase company name pushes the
                          mobile trust badge onto its own line below the name
                          instead of overflowing off the right screen edge. */}
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="font-semibold text-ink transition-colors group-hover:text-brand">
                          {r.name ?? r.tin}
                        </span>
                        <GoodStandingBadge
                          goodStanding={r.goodStanding}
                          kind={countsAsOf(r)}
                          className="shrink-0 sm:hidden"
                        />
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[12px] text-ink-muted">
                        <span className="tabular-nums">{r.tin}</span>
                        {/* The company's role in contracts, stated on its own
                            — the badge's «Действующий» is the tax registry's
                            word, not the role (owner, 2026-10-14). */}
                        <span aria-hidden className="text-edge">
                          ·
                        </span>
                        <span className="font-medium text-ink">
                          {t(`companies.rowRole.${r.role}`)}
                        </span>
                        {r.sector && (
                          <>
                            <span aria-hidden className="text-edge">
                              ·
                            </span>
                            <span className="truncate">{r.sector}</span>
                          </>
                        )}
                        {r.region && (
                          <>
                            <span aria-hidden className="text-edge">
                              ·
                            </span>
                            <span>{localizeRegion(r.region, locale)}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Metrics: contract count + total value, right-aligned on
                      desktop, inline under the name on mobile */}
                  <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 pl-9 sm:flex-col sm:flex-nowrap sm:items-end sm:gap-1.5 sm:pl-0 sm:text-right">
                    <GoodStandingBadge
                      goodStanding={r.goodStanding}
                      kind={countsAsOf(r)}
                      className="order-last hidden sm:block"
                    />
                    <span className="text-[13px] font-semibold tabular-nums text-ink">
                      {r.countsAs
                        ? t(`companies.contractsAs.${r.countsAs}`, { count: r.totalContracts })
                        : t('companies.contracts', { count: r.totalContracts })}
                    </span>
                    {r.totalValueUzs > 0 && (
                      <span className="text-[12px] tabular-nums text-ink-muted">
                        {withCurrency(
                          formatBudgetCompact(String(r.totalValueUzs), locale),
                          t('tender.budget.unit'),
                        )}
                      </span>
                    )}
                    {(() => {
                      const other = otherCapacity(r);
                      return other ? (
                        <span className="text-[12px] tabular-nums text-ink-muted">
                          {t(`companies.contractsAs.${other.as}`, { count: other.count })}
                        </span>
                      ) : null;
                    })()}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}

        {total > PAGE && (
          <Pagination
            page={page}
            limit={PAGE}
            total={total}
            onPageChange={goToPage}
            // Crawlable `/companies/page/N` links for the unfiltered directory;
            // filtered views fall back to `?page=N` via onPageChange.
            hrefFor={anyFilter ? undefined : (n) => listingPageHref('/companies', n)}
          />
        )}
      </div>
    </>
  );
}
